import { useContext, useEffect, useRef } from "react";
import { SocketContext } from "../../../context/Socket/SocketContext";

function messageIsInternalTicketAction(msg) {
  if (!msg || typeof msg !== "object") return false;
  const mt = msg.mediaType;
  if (mt === "internal_action" || mt === "internalAction") return true;
  const id = msg.id;
  return typeof id === "string" && id.startsWith("internal_action_");
}

const useNotificationsSocket = ({
  user,
  applySocketMessage,
  applyTicketEvent,
  applyContactDelete,
  onNewMessage,
  onIncomingTransfer,
  onNewWebLead,
  onModoSeguroLead,
  fetchNotifications,
}) => {
  const socketManager = useContext(SocketContext);
  const handlersRef = useRef({ applySocketMessage, applyTicketEvent, applyContactDelete, onNewMessage, onIncomingTransfer, onNewWebLead, onModoSeguroLead, fetchNotifications, user });

  useEffect(() => {
    handlersRef.current = { applySocketMessage, applyTicketEvent, applyContactDelete, onNewMessage, onIncomingTransfer, onNewWebLead, onModoSeguroLead, fetchNotifications, user };
  });

  useEffect(() => {
    if (!user?.companyId) return;
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);
    if (!socket?.socket) return;

    const handleAppMessage = (data) => {
      const currentUser = handlersRef.current.user;
      const ticketUserId = data.ticket?.userId;

      if (!data?.message || !data?.ticket) return;
      if (data.message.fromMe) return;
      if (messageIsInternalTicketAction(data.message)) return;

      if (ticketUserId && ticketUserId !== currentUser?.id && currentUser?.profile !== "admin") {
        return;
      }

      handlersRef.current.applySocketMessage(data);
      handlersRef.current.onNewMessage?.(data);
    };

    const handleTicketUpdate = (data) => {
      /**
       * Trabajo que ENTRA por transferencia: suena igual que un mensaje nuevo.
       *
       * WHY se mira `data.traspaso` y no se compara la lista anterior: el
       * servidor es el único que sabe qué cambió de verdad. `action: "update"`
       * se emite al aceptar, cerrar, etiquetar o marcar leído, así que sonar con
       * todos ellos convertiría el panel en una alarma continua.
       *
       * El eco propio se descarta aquí, no en el servidor: el evento va a las
       * salas del departamento y quien lo transfirió puede estar en una de
       * ellas. Cada navegador sabe quién es; el servidor solo dice quién lo hizo.
       */
      const t = data?.traspaso;
      if (t && Number(t.porUsuarioId) !== Number(handlersRef.current.user?.id)) {
        handlersRef.current.onIncomingTransfer?.({ ...data, traspaso: t });
      }

      if (data?.action === "updateUnread" && data?.ticketId) {
        const count = typeof data.unreadMessages === "number" ? data.unreadMessages : 0;
        if (count === 0) {
          handlersRef.current.applyTicketEvent({
            action: "update",
            ticket: { id: data.ticketId, unreadMessages: 0 },
          });
        }
        return;
      }
      if (!data?.ticket) return;

      /**
       * Un ticket-lead web NUNCA pasa por `appMessage` (no hay ningún mensaje
       * que haya ENTRADO — el "mensaje" es un envío de formulario, no un
       * WhatsApp real), así que sin esto un agente nunca se entera de un lead
       * nuevo salvo que esté mirando la pantalla en ese instante. Solo en la
       * creación (`action === "create"`), nunca en cada actualización — mismo
       * criterio que ya evita que un `action:"update"` cualquiera dispare la
       * alarma.
       */
      if (data.action === "create" && data.ticket?.origin === "web_lead") {
        handlersRef.current.onNewWebLead?.(data);
      }

      handlersRef.current.applyTicketEvent(data);
    };

    const handleContactEvent = (data) => {
      if (data?.action === "delete" && data?.contactId) {
        handlersRef.current.applyContactDelete?.(data.contactId);
        return;
      }
      /**
       * Lead en modo seguro (0417, Punto 4 — mejora post-cierre): se vinculó
       * a un Contacto pero NUNCA se creó ticket, así que no hay ningún
       * `company-{id}-ticket` que avisar — sin esto, el agente nunca se
       * entera y tiene que revisar "Solicitudes web" a mano (hallazgo real
       * del propietario probando en el VPS).
       */
      if (data?.action === "web_lead_linked") {
        handlersRef.current.onModoSeguroLead?.(data);
      }
    };

    const handleReady = () => {
      socket.emit("joinNotification");
      handlersRef.current.fetchNotifications?.();
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);
    socket.on(`company-${companyId}-contact`, handleContactEvent);

    if (socket.socket.connected) {
      socket.emit("joinNotification");
    }

    return () => {
      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
      socket.off(`company-${companyId}-contact`, handleContactEvent);
    };
  }, [user?.companyId, socketManager]);
};

export default useNotificationsSocket;
